<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
      @click.self="cancel"
    >
      <div
        class="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 ease-out"
        :class="{ 'scale-100 opacity-100': isVisible, 'scale-95 opacity-0': !isVisible }"
      >
        <!-- En-tête avec icône -->
        <div class="p-6 pb-4">
          <div class="flex items-center space-x-4">
            <div 
              class="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              :class="iconClasses"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="type === 'danger'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                <path v-else-if="type === 'warning'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path v-else-if="type === 'info'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900">{{ title }}</h3>
              <p v-if="subtitle" class="text-sm text-gray-500 mt-1">{{ subtitle }}</p>
            </div>
          </div>
        </div>

        <!-- Contenu -->
        <div class="px-6 pb-6">
          <div class="text-gray-600 text-sm leading-relaxed">
            <slot>
              <p>{{ message }}</p>
            </slot>
          </div>

          <!-- Détails supplémentaires -->
          <div v-if="details" class="mt-4 p-3 bg-gray-50 rounded-lg">
            <p class="text-xs text-gray-600">{{ details }}</p>
          </div>

          <!-- Actions -->
          <div class="flex items-center justify-end space-x-3 mt-6">
            <button
              @click="cancel"
              :disabled="loading"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
            >
              {{ cancelText }}
            </button>
            
            <button
              @click="confirm"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-colors"
              :class="confirmButtonClasses"
            >
              <svg 
                v-if="loading" 
                class="animate-spin w-4 h-4 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              
              <svg 
                v-else-if="type === 'danger'" 
                class="w-4 h-4 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              
              {{ loading ? loadingText : confirmText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'

interface Props {
  isVisible: boolean
  type?: 'danger' | 'warning' | 'info' | 'success'
  title: string
  subtitle?: string
  message?: string
  details?: string
  confirmText?: string
  cancelText?: string
  loadingText?: string
  loading?: boolean
}

interface Emits {
  (e: 'confirm'): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  type: 'warning',
  confirmText: 'Confirmer',
  cancelText: 'Annuler',
  loadingText: 'Traitement...',
  loading: false
})

const emit = defineEmits<Emits>()

const iconClasses = computed(() => {
  const classes = {
    danger: 'bg-red-100 text-red-600',
    warning: 'bg-yellow-100 text-yellow-600',
    info: 'bg-blue-100 text-blue-600',
    success: 'bg-green-100 text-green-600'
  }
  return classes[props.type]
})

const confirmButtonClasses = computed(() => {
  const classes = {
    danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
    info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    success: 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
  }
  return classes[props.type]
})

const confirm = () => {
  if (!props.loading) {
    emit('confirm')
  }
}

const cancel = () => {
  if (!props.loading) {
    emit('cancel')
  }
}

// Gestion des touches clavier
const handleKeydown = (event: KeyboardEvent) => {
  if (!props.isVisible) return
  
  if (event.key === 'Escape') {
    cancel()
  } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
    confirm()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Animation pour l'entrée/sortie */
.v-enter-active,
.v-leave-active {
  transition: all 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>